import React, { useState } from 'react';
import { Grid, useMediaQuery, Typography, Tooltip, Button, Divider, Box, Badge } from '@mui/material';
import { useTheme } from '@mui/system';
import Confetti from 'react-confetti';
import { InfoOutlined } from '@mui/icons-material';
import { variants } from '../../../../variants/variants';
import UserAvatar from '../../../../common/UserAvatar';
import Carousel from 'react-material-ui-carousel';

const SmallWinnerCard = ({ winner, setSelectedWinner, setOpen }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [hovered, setHovered] = useState(false);

  const handleOverviewClick = () => {
    setSelectedWinner(winner);
    setOpen(true);
  };

  if (!winner) return <>Loading</>;

  return (
    <Box width={1}>
      <>
        <Badge
          badgeContent={winner?.categoryName || winner?.winnerTeam}
          sx={{
            position: 'relative',
            top: '10px',
            left: '60px',
            '& .MuiBadge-badge': {
              position: 'absolute',
              top: '0',
              right: '0',
              padding: isSm ? '7px' : '10px',
              bgcolor: '#EEF5FF',
              color: '#0a4fac',
              height: 'auto',
              minWidth: isSm ? '90px' : '100px',
              fontSize: isSm ? '12px' : '14px',
              fontWeight: 'bold',
            },
          }}
        />

        {winner.isTeamNomination ? (
          // Team Nomination Card
          <Grid
            sx={{
              wrap: 'nowrap',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              boxShadow: 'none',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              borderRadius: '8px',
              textAlign: 'center',
              transition: 'all 0.1s ease-in-out',
              height: isSm ? '220px' : '35vh',
              '&:hover': {
                transform: 'scale(1.0)',
                boxShadow: variants.BOX_SHADOW,
              },
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {hovered && <Confetti numberOfPieces={200} width={300} height={310} recycle={true} />}

            <Box
              sx={{ whiteSpace: 'nowrap' }}
              width={'100%'}
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Button
                onClick={handleOverviewClick}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: isSm ? -10 : 0,
                  zIndex: 1,
                }}
              >
                <Tooltip title='Overview'>
                  <InfoOutlined />
                </Tooltip>
              </Button>
            </Box>

            <Grid item xl={12} width={'100%'} overflow={'hidden'}>
              <Carousel autoPlay interval={2000} sx={{ height: '160px' }} indicators={false}>
                {winner.winnerTeam?.teamMembers.map((teamMember, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      overflow: 'none',
                      wordWrap: 'break-word',
                    }}
                  >
                    <UserAvatar src={teamMember?.photo} sx={{ width: 80, height: 80, mb: 1, mt: 0 }} />
                    <Typography
                      variant='h6'
                      sx={{
                        mb: isSm ? 0 : 1,
                        overflowWrap: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '1rem',
                        },
                      }}
                    >
                      {teamMember?.firstName} {teamMember?.lastName}
                    </Typography>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        overflowWrap: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '0.8rem',
                        },
                        color: 'grey',
                      }}
                    >
                      {teamMember?.department}
                    </Typography>
                    <Typography
                      variant='subtitle1'
                      sx={{
                        overflowWrap: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        [theme.breakpoints.down('sm')]: {
                          fontSize: '0.8rem',
                        },
                        color: 'grey',
                      }}
                    >
                      {teamMember?.designation}
                    </Typography>
                  </Box>
                ))}
              </Carousel>

              <Divider sx={{ width: '100%', mb: isSm ? 0 : 1 }} />

              <Typography
                color='text.secondary'
                sx={{
                  overflowWrap: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '0.9rem',
                  },
                }}
                variant='body1'
              >
                {winner.winnerTeam.teamName}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          // Individual Nomination Card
          <Grid
            sx={{
              wrap: 'nowrap',
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              boxShadow: 'none',
              padding: 2,
              position: 'relative',
              borderRadius: '8px',
              textAlign: 'center',
              transition: 'all 0.1s ease-in-out',
              height: isSm ? '170px' : '35vh',
              '&:hover': {
                transform: 'scale(1.0)',
                boxShadow: variants.BOX_SHADOW,
              },
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {hovered && <Confetti numberOfPieces={200} width={300} height={310} recycle={true} />}

            <Tooltip title='Overview'>
              <Button
                onClick={handleOverviewClick}
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: isSm ? -10 : 0,
                  zIndex: 1,
                }}
              >
                <InfoOutlined />
              </Button>
            </Tooltip>

            <Box display={'flex'} width={'90%'}>
              <UserAvatar
                src={winner?.winner?.photo}
                sx={{ width: isSm ? 60 : 80, height: isSm ? 60 : 80, mb: 2, mt: 1 }}
              />
              <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                p={1}
                flex={1}
              >
                <Typography
                  variant='h6'
                  sx={{
                    overflowWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '1rem',
                    },
                  }}
                >
                  {winner?.winner.empName}
                </Typography>
                <Typography
                  variant='subtitle1'
                  sx={{
                    overflowWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '0.8rem',
                    },
                    color: 'grey',
                  }}
                >
                  {winner?.winner.empCode}
                </Typography>
                <Typography
                  noWrap
                  variant='subtitle1'
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '0.8rem',
                    },
                    color: 'grey',
                    maxWidth: '120px',
                  }}
                >
                  {winner?.winner.department}
                </Typography>
              </Box>
            </Box>

            <Typography
              noWrap
              variant='subtitle1'
              color='text.secondary'
              sx={{
                mb: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                [theme.breakpoints.down('sm')]: {
                  fontSize: '0.8rem',
                },
                maxWidth: '300px',
              }}
            >
              {winner?.winner.designation}
            </Typography>
          </Grid>
        )}
      </>
    </Box>
  );
};

export default SmallWinnerCard;
